import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import {
  errorNotification,
  successNotification,
  vehicleHistoryAccountRefreshUrl,
  vehicleHistoryAccountUrl,
  vehicleHistoryAlertClearUrl,
  vehicleHistoryAlertsUrl,
  vehicleHistoryPriceChangesUrl,
  vehicleHistoryServiceUrl,
  vehicleHistoryServicesUrl
} from '../../config'
import { getQuiet, patchQuiet, postQuiet } from './api'
import {
  COST_SOURCE_LABELS,
  PRIORITY_COLORS,
  alertLabel,
  formatAge,
  formatDateTime,
  percent,
  serverMessage,
  usd
} from './utils'

// Everything about our GlobalVIN account on one screen: what we have to spend, what each report
// costs, what has changed, and what the team is being told about.
//
// The sentence that runs through this page: **none of it stops a sale.** The owner asked for the
// alerts so nobody gets stuck after paying, not so the shop closes when credit runs low.
const SupplierPage = () => {
  const [account, setAccount] = useState(null)
  const [services, setServices] = useState([])
  const [changes, setChanges] = useState([])
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const load = useCallback(async () => {
    setLoading(true)
    const [accountResponse, servicesResponse, changesResponse, alertsResponse] =
      await Promise.all([
        getQuiet(vehicleHistoryAccountUrl),
        getQuiet(vehicleHistoryServicesUrl),
        getQuiet(vehicleHistoryPriceChangesUrl),
        getQuiet(vehicleHistoryAlertsUrl)
      ])
    setLoading(false)
    if (accountResponse.success) {
      setAccount(accountResponse.data)
      form.setFieldsValue((accountResponse.data || {}).thresholds || {})
    }
    if (servicesResponse.success) setServices((servicesResponse.data || {}).results || [])
    if (changesResponse.success) setChanges((changesResponse.data || {}).results || [])
    if (alertsResponse.success) setAlerts((alertsResponse.data || {}).results || [])
  }, [form])

  useEffect(() => {
    load()
  }, [load])

  const refreshNow = async () => {
    setSaving(true)
    const response = await postQuiet(vehicleHistoryAccountRefreshUrl, {})
    setSaving(false)
    if (!response.success) {
      errorNotification(serverMessage(response, 'Could not reach GlobalVIN.'))
      return
    }
    successNotification('Read from GlobalVIN.')
    load()
  }

  const saveThresholds = async values => {
    setSaving(true)
    const response = await patchQuiet(vehicleHistoryAccountUrl, values)
    setSaving(false)
    if (!response.success) {
      errorNotification(serverMessage(response, 'Could not save those thresholds.'))
      return
    }
    setAccount(response.data)
    successNotification('Saved.')
  }

  const editCost = service => {
    let value = service.costUsd === null ? null : Number(service.costUsd)
    Modal.confirm({
      title: `What does ${service.name} cost?`,
      content: (
        <div>
          <Typography.Paragraph type="secondary">
            For a report GlobalVIN does not publish a price for. Recorded as entered by hand, which
            the price sync and the seed both leave alone.
          </Typography.Paragraph>
          <InputNumber
            defaultValue={value}
            min={0}
            precision={2}
            style={{ width: '100%' }}
            onChange={next => {
              value = next
            }}
          />
        </div>
      ),
      okText: 'Save',
      onOk: async () => {
        const response = await patchQuiet(vehicleHistoryServiceUrl(service.id), {
          costUsd: value === null || value === undefined ? null : Number(value).toFixed(2)
        })
        if (!response.success) {
          errorNotification(serverMessage(response, 'Could not save that cost.'))
          return
        }
        successNotification('Saved.')
        load()
      }
    })
  }

  const clearAlert = alert => {
    let note = ''
    Modal.confirm({
      title: `Clear "${alertLabel(alert.kind)}"?`,
      content: (
        <textarea
          rows={3}
          style={{ width: '100%', marginTop: 8 }}
          placeholder="What was done about it."
          onChange={event => {
            note = event.target.value
          }}
        />
      ),
      okText: 'Clear it',
      onOk: async () => {
        const response = await postQuiet(vehicleHistoryAlertClearUrl(alert.id), { note })
        if (!response.success) {
          errorNotification(serverMessage(response, 'Could not clear that alert.'))
          return
        }
        load()
      }
    })
  }

  const thresholds = (account || {}).thresholds || {}
  const openAlerts = alerts.filter(alert => !alert.clearedAt)
  const pastAlerts = alerts.filter(alert => alert.clearedAt)

  /* eslint-disable react/display-name */
  const serviceColumns = [
    {
      title: 'Report',
      key: 'name',
      render: (_, row) => (
        <div>
          <div>{row.name}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.family}/{row.key}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'Costs us',
      key: 'cost',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>
            {row.costUsd === null ? (
              <Typography.Text type="warning">Not known yet</Typography.Text>
            ) : (
              usd(row.costUsd)
            )}
          </span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {COST_SOURCE_LABELS[row.costSource] || '—'}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'On the public list',
      key: 'listed',
      render: (_, row) =>
        row.vanishedAt ? (
          <Tag color="orange">No longer listed</Tag>
        ) : row.listedPublicly ? (
          <Tag color="green">Listed</Tag>
        ) : (
          // Not a fault: GlobalVIN has never published the Global/OEM or Japanese prices, which
          // is exactly why what we were charged is the only signal they changed.
          <Tag>Never listed</Tag>
        )
    },
    {
      title: 'Used by',
      key: 'usedBy',
      render: (_, row) => (row.usedBy || []).join(', ') || <Typography.Text type="secondary">—</Typography.Text>
    },
    {
      title: 'Health',
      key: 'health',
      render: (_, row) =>
        row.failureStreak ? (
          <Space direction="vertical" size={0}>
            <Tag color={row.failureStreak >= 3 ? 'red' : 'orange'}>
              {row.failureStreak} failures in a row
            </Tag>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.lastError}
            </Typography.Text>
          </Space>
        ) : (
          <Tag color="green">Fine</Tag>
        )
    },
    {
      title: '',
      key: 'edit',
      render: (_, row) =>
        row.listedPublicly ? null : (
          <Button type="link" size="small" onClick={() => editCost(row)}>
            Set cost
          </Button>
        )
    }
  ]

  const changeColumns = [
    { title: 'When', key: 'when', render: (_, row) => formatDateTime(row.detectedAt) },
    { title: 'Report', dataIndex: 'serviceName', key: 'service' },
    {
      title: 'Moved',
      key: 'moved',
      render: (_, row) => (
        <span>
          {row.oldCostUsd === null ? 'new' : usd(row.oldCostUsd)} →{' '}
          {row.newCostUsd === null ? 'gone' : usd(row.newCostUsd)}
        </span>
      )
    },
    {
      title: 'How we found out',
      key: 'source',
      render: (_, row) =>
        row.source === 'charged' ? (
          <Tag color="orange">From an order we paid for</Tag>
        ) : (
          <Tag>Public price list</Tag>
        )
    },
    {
      title: 'What it did to our margins',
      key: 'affected',
      render: (_, row) =>
        (row.affected || []).length ? (
          <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
            {(row.affected || []).map(entry => (
              <li key={entry.productKey}>
                {entry.name}: {percent(entry.marginBefore)} → {percent(entry.marginAfter)}
              </li>
            ))}
          </ul>
        ) : (
          <Typography.Text type="secondary">No package uses it</Typography.Text>
        )
    }
  ]

  const alertColumns = cleared => [
    {
      title: 'What',
      key: 'kind',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Tag color={PRIORITY_COLORS[row.priority] || 'default'}>{alertLabel(row.kind)}</Tag>
          <Typography.Text style={{ fontSize: 12 }}>{row.detail}</Typography.Text>
        </Space>
      )
    },
    { title: 'Raised', key: 'raised', render: (_, row) => formatAge(row.raisedAt) + ' ago' },
    {
      title: 'Told us',
      key: 'notified',
      render: (_, row) =>
        row.notifyCount ? `${row.notifyCount}× · ${formatAge(row.lastNotifiedAt)} ago` : '—'
    },
    cleared
      ? {
        title: 'Cleared',
        key: 'cleared',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <span>{formatDateTime(row.clearedAt)}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.clearedBy} {row.note ? `· ${row.note}` : ''}
            </Typography.Text>
          </Space>
        )
      }
      : {
        title: '',
        key: 'clear',
        render: (_, row) => (
          <Button size="small" onClick={() => clearAlert(row)}>
            Clear
          </Button>
        )
      }
  ]
  /* eslint-enable react/display-name */

  return (
    <div>
      <Card
        title="GlobalVIN account"
        style={{ marginBottom: 16 }}
        extra={
          <Button icon={<ReloadOutlined />} loading={saving || loading} onClick={refreshNow}>
            Refresh now
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Statistic
              title="Our credit"
              value={
                (account || {}).creditBalanceUsd === null ||
                (account || {}).creditBalanceUsd === undefined
                  ? '—'
                  : usd(account.creditBalanceUsd)
              }
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              read {formatAge((account || {}).balancesSeenAt)} ago
            </Typography.Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="Carfax credits"
              value={
                (account || {}).carfaxCredits === null ||
                (account || {}).carfaxCredits === undefined
                  ? '—'
                  : account.carfaxCredits
              }
            />
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="GlobalVIN's own USA supply"
              value={
                (account || {}).upstreamUsaBalance === null ||
                (account || {}).upstreamUsaBalance === undefined
                  ? '—'
                  : usd(account.upstreamUsaBalance)
              }
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Their stock, not ours. We cannot top it up.
            </Typography.Text>
          </Col>
          <Col xs={12} md={6}>
            <Statistic
              title="USA service"
              value={(account || {}).usaHealth === 'ok' ? 'Up' : (account || {}).usaHealth || '—'}
              valueStyle={{
                color: (account || {}).usaHealth === 'ok' ? '#3f8600' : undefined
              }}
            />
          </Col>
        </Row>

        {(account || {}).pricingLastError ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
            message="The last price-list read failed"
            description={account.pricingLastError}
          />
        ) : null}
      </Card>

      <Card title="When to warn us" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Alerts only. Nothing stops selling."
          description="A customer can always buy a report. If our credit runs out, their order waits and completes itself the moment you top up."
        />
        <Form form={form} layout="inline" onFinish={saveThresholds} initialValues={thresholds}>
          <Form.Item label="Low balance" name="lowBalanceUsd">
            <InputNumber min={0} precision={2} prefix="$" />
          </Form.Item>
          <Form.Item label="Critical balance" name="criticalBalanceUsd">
            <InputNumber min={0} precision={2} prefix="$" />
          </Form.Item>
          <Form.Item label="Low Carfax credits" name="lowCarfaxCredits">
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item label="Low upstream supply" name="lowUpstreamUsaBalance">
            <InputNumber min={0} precision={2} prefix="$" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="What each report costs us" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={services}
          columns={serviceColumns}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Card title="Open alerts" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={openAlerts}
          columns={alertColumns(false)}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nothing needs attention.' }}
        />
      </Card>

      <Card title="Price changes" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={changes}
          columns={changeColumns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No prices have moved since we started watching.' }}
        />
      </Card>

      <Card title="Alerts already dealt with">
        <Table
          rowKey="id"
          size="small"
          dataSource={pastAlerts}
          columns={alertColumns(true)}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nothing yet.' }}
        />
      </Card>
    </div>
  )
}

export default SupplierPage
